import { DrawerBase } from 'bog-arcade-core-ui/core/DrawerBase';
import { delay, random, shuffle } from 'bog-arcade-core-ui/helpers';
import { gsap } from 'gsap';
import { BallCrackType, ballSides, BallType, GameLayers } from '../../../core/enums';
import { ballHelper } from '../../../core/ball/ballHelper';
import { colors } from '../../../core/constats';
import { bezierCurve } from '../../../core/bezierCurve/BezierCurve';
import { BallAnimations } from '../../../core/bezierCurve/BallAnimations';
import { gamePadService } from '../../../services/helperServices/GamePadService';
import { ACTIVE_ODD, BONUS_BALL_CRACK } from '../../../stateManagment';
import { gameAnimations } from '../../../services/helperServices/GameAnimationService';
import { backgroundDrawer } from '../../background/BackgroundDrawer';
import { bonusSpin } from '../../gamePad/events';
import { bigWinService } from '../../../services/helperServices/BigWinService';
import { bonusSpinService } from '../../../services/helperServices/BonusSpinService';

export class BaseBallDrawer extends DrawerBase {
  constructor() {
    super();
    this.elements = [null, null];
    this.leftColors = shuffle([...colors]);
    this.rightColors = shuffle([...colors]);
    this.ballContainer = this.createContainer('ballContainer');
    this.leftBalls = [];
    this.rightBalls = [];
    this.rightAdditionalBalls = [];
    this.leftAdditionalBalls = [];
    this.postion = true;
    this.brokenBallsCount = 0;
    this.turn = 0;
    this._ballState = BallCrackType.none;
    this.brokenBalls = [];
    this.step = 4;
  }

  _updateLeftBalls(color) {
    this.elements[0] = this.leftBalls[this.leftBalls.length - 1];
    this.leftBalls.pop();
    this.container[GameLayers.balls].removeChildren();

    const ball = ballHelper.getBall(color, BallCrackType.none);
    ball.scale.set(.1);
    ball.alpha = 0;
    this.setPosition(ball, this.positions.left.start);

    this.container[GameLayers.balls].addChild(...[ball, ...this.leftBalls, ...this.rightBalls], this.ballContainer);
    this.leftBalls.forEach((ball, index) => {
      this.leftBallAnimation.moveElements(ball, index, index + 1, index ? .15 : 0);
    });
    this.leftBallAnimation.moveElements(ball, 0, 0, 0);

    this.leftBalls.unshift(ball);
  }

  _updateRightBalls(color) {
    this.elements[1] = this.rightBalls[this.rightBalls.length - 1];
    this.rightBalls.pop();
    this.container[GameLayers.balls].removeChildren();

    const ball = ballHelper.getBall(color, BallCrackType.none);
    ball.scale.set(.1);
    ball.alpha = 0;
    this.setPosition(ball, this.positions.right.start);

    this.container[GameLayers.balls].addChild(...[ball, ...this.rightBalls, ...this.leftBalls], this.ballContainer);
    this.rightBalls.forEach((ball, index) => {
      this.rightBallAnimation.moveElements(ball, index, index + 1, index ? .15 : 0, -8);
    });
    this.rightBallAnimation.moveElements(ball, 0, 0, 0);

    this.rightBalls.unshift(ball);
  }

  setBalls() {
    return Promise.all([
      this._setLeftBalls(),
      this._setRightBalls(),
    ]).then(() => {
      gamePadService.enableAll();
    });
  }

  __setCurve() {
    this.leftPoints = bezierCurve.getPoints(100,
      this.positions.left.start,
      this.positions.left.curve,
      this.positions.left.end,
    );

    this.rightPoints = bezierCurve.getPoints(100,
      this.positions.right.start,
      this.positions.right.curve,
      this.positions.right.end,
    );

    this.leftBallAnimation = new BallAnimations({
      scales: this._scales,
      positions: this._positions,
      lastScale: this._lastScale,
    });
    this.leftBallAnimation.setPoints(this.leftPoints);

    this.rightBallAnimation = new BallAnimations({
      scales: this._scales,
      positions: this._positions,
      lastScale: this._lastScale,
    });

    this.rightBallAnimation.setPoints(this.rightPoints);
  }

  setupLayers(layers) {
    this.container = {
      [GameLayers.balls]: layers[GameLayers.balls],
      [GameLayers.ball]: layers[GameLayers.ball],
    };
  }

  __getBalls(color, crackState) {
    const ball = ballHelper.getBall(color, crackState);
    ball.scale.set(.1);
    ball.alpha = 0;
    return ball;
  }

  _setLeftBalls(shuffledColors = this.leftColors, step = 0, cracksState = []) {
    return new Promise((resolve) => {
      shuffledColors.map((color, index, { length }) => {
        const crackState = cracksState[index] ? cracksState[index] : BallCrackType.none;
        const ball = this.__getBalls(color, crackState);
        this.leftBalls.push(ball);
        ball.turn = index;
        this.setPosition(ball, this.positions.left.start);
        this.leftBallAnimation.moveElements(ball, 0, index + step, .15 * (length - index), 8).then(() => {
          if (length - index === 1) {
            resolve(true);
            this.elements[1] = ball;
          }
          if(step) {
            backgroundDrawer.application.setAllBreakingBallsSpine({ x: ball.position.x, y: ball.position.y });
          }
        });
        if (length - index === 1) {
          this.elements[0] = ball;
        }
        this.container[GameLayers.balls].addChild(ball);
      });
    });
  }

  _setAdditionalLeftBalls(additionalBalls, color, step = this.step) {
    const newBalls = [];
    return new Promise((resolve) => {
      if (!additionalBalls?.length) {
        delay(() => {
          resolve(true);
        }, 1000);
      }
      additionalBalls.map((ball, index, { length }) => {
        const newBall = this.__getBalls(ball.ball, ball.crackedCount);
        newBalls.push(newBall);
        newBall.turn = index;
        this.setPosition(newBall, this.positions.left.start);
        this.leftBallAnimation.moveElements(newBall, 0, index + step, .15 * (length - index), 8).then(() => {
          resolve(true);
        });
        if (length - index === 1) {
          this.elements[0] = newBall;
        }
        this.container[GameLayers.balls].addChildAt(newBall, index);
      });
      this.leftAdditionalBalls = [];
      this.leftBalls = [...newBalls, ...this.leftBalls];
      this.turn = 0;
    });
  }

  _setRightBalls(shuffledColors = this.rightColors, step = 0, cracksState = []) {
    return new Promise((resolve) => {
      shuffledColors.map((color, index, { length }) => {
        const crackState = cracksState[index] ? cracksState[index] : BallCrackType.none;
        const ball = this.__getBalls(color, crackState);
        this.rightBalls.push(ball);
        ball.turn = step + length - index - 1;
        this.setPosition(ball, this.positions.right.start);
        this.rightBallAnimation.moveElements(ball, 0, index + step, .15 * (length - index), -8).then(() => {
          if (length - index === 1) {
            resolve(true);
            this.elements[1] = ball;
          }
          if(step) {
            backgroundDrawer.application.setAllBreakingBallsSpine({ x: ball.position.x, y: ball.position.y });
          }
        });
        this.container[GameLayers.balls].addChild(ball);
      });
    });
  }

  _setAdditionalRightBalls(additionalBalls, color, step = this.step) {
    const newBalls = [];
    return new Promise((resolve) => {
      if (!additionalBalls?.length) {
        delay(() => {
          resolve(true);
        }, 1000);
      }
      additionalBalls.reverse().map((ball, index, { length }) => {
        const newBall = this.__getBalls(ball.ball);
        newBall.turn = step + length - index - 1;
        newBalls.push(newBall);
        this.setPosition(newBall, this.positions.right.start);
        this.rightBallAnimation.moveElements(newBall, 0, index + step, .15 * (length - index), -8).then(() => {
          resolve(true);
        });
        if (length - index === 1) {
          this.elements[0] = newBall;
        }
        this.container[GameLayers.balls].addChildAt(newBall, this.step + index);
      });
      this.rightAdditionalBalls = [];
      this.rightBalls = [...newBalls, ...this.rightBalls];
    });
  }

  setAdditionalBalls() {
    return Promise.all([
      this._setAdditionalLeftBalls(this.leftAdditionalBalls),
      this._setAdditionalRightBalls(this.rightAdditionalBalls),
    ]);
  }

  switch() {
    return new Promise(resolve => {
      const side = +random(0, 2);
      const ball = this.elements[side];
      const sideList = side === 0 ? this.leftColors : this.rightColors;

      const newBallColor = sideList.pop();
      sideList.unshift(newBallColor);

      this.postion = !this.postion;
      gsap.to(ball, { duration: .05, pixi: { scale: 0 } });
      gsap.to(this.ball, {
        duration: .05,
        pixi: { scale: 0 },
        onComplete: () => {
          if (side === 0) {
            this._updateLeftBalls(newBallColor);
          } else {
            this._updateRightBalls(newBallColor);
          }
          this.setBall(newBallColor, resolve);
        },
      });
    });
  }

  get randomColor() {
    return random(1, 5);
  }

  hideBall() {
    return delay(() => {
      this.ball.visible = false;
    }, 150);
  }

  hideBrokenBall(ball) {
    return new Promise(resolve => {
      gsap.to(ball, {
        delay: .5,
        duration: .2,
        pixi: {
          scale: 0,
        },
        onComplete: () => {
          if (ball) {
            resolve(true);
            ball.visible = false;
          }
        },
      });
    });
  }


  crackBallWhileBonus(ball, index, crackState) {
    return new Promise((resolve) => {
      const newBall = ballHelper.getBall(ball.color, crackState);
      newBall.crackState = crackState;
      newBall.scale.set(ball.scale.x);
      this.setPosition(newBall, { x: ball.position.x, y: ball.position.y });
      this.container[GameLayers.balls].removeChild(ball);
      this.container[GameLayers.balls].addChildAt(newBall, index);
      resolve(newBall);
    });
  }

  breakBonusBall() {
    return new Promise(resolve => {
      this.postion = !this.postion;
      gsap.to(this.ball, {
        delay: .5,
        duration: .05,
        pixi: { scale: 0 },
        onComplete: () => {
          resolve(true);
        },
      });
    });
  }

  breakBall() {
    return new Promise(resolve => {
      const side = +random(0, 2);
      const sideList = side === 0 ? this.leftColors : this.rightColors;
      const newBallColor = sideList.pop();
      sideList.unshift(newBallColor);
      this.postion = !this.postion;
      gsap.to(this.ball, {
        duration: .05,
        pixi: { scale: 0 },
        onComplete: () => {
          if (side === 0) {
            this._updateLeftBalls(newBallColor);
          } else {
            this._updateRightBalls(newBallColor);
          }
          this.setBall(newBallColor, resolve);
        },
      });
    });
  }

  breakBallsAnimation(side, brokenBalls, animationSide, odds, crackStates, multiplier, setWin = true) {
    const nextBalls = [];
    const length = brokenBalls.length;
    let brokenBallsCurrentCount = 0;
    let odd = 0;
    this[side].forEach((ball, index) => {
      if (brokenBalls[index] !== undefined) {
        this[side][brokenBalls[index]].broken = true;
      }
    });

    return new Promise(resolve => {
      this[side].forEach((ball, index) => {
        const ballTurn = side === ballSides.right ? index + this.step : index;
        const winTurn = side === ballSides.right ? this.brokenBallsCount - brokenBallsCurrentCount - 1 : this.turn;
        const newPosition = ballHelper.getNewPosition(index, this.step, length, brokenBallsCurrentCount);
        if (!ball.broken) {
          this.crackBallWhileBonus(ball, ballTurn, crackStates[index])
            .then((newBall) => {
                nextBalls.push(newBall);
                delay(() => {
                  animationSide.moveElements(newBall, index + this.step, newPosition);
                  if (!brokenBalls.length) {
                    resolve(true);
                  }
                }, 1300);
              },
            );
        } else {
          this.turn++;
          odd = odds[brokenBallsCurrentCount];
          brokenBallsCurrentCount++;
          delay(() => {
            gameAnimations.breakCustomBallBoom(ballTurn);
            this.hideBrokenBall(ball);
          }, 100);
          backgroundDrawer.application.setBallBreakWin(
            odd,
            winTurn,
            this.brokenBallsCount,
            side,
            { x: ball.position.x, y: ball.position.y }).then(() => {
            resolve(true);
          });
        }
        if (index === 3) {
          this[side] = nextBalls;
        }
      });
    });
  }

  breakBonusBalls({ result, additionalInfo, odd, winAmount, multiplayer }) {
    const multiplier = multiplayer;

    const leftOdds = [];
    const rightOdds = [];
    const leftBrokenBallsIndexes = [];
    const rightBrokenBallsIndexes = [];
    const leftCrackStates = [];
    const rightCrackStates = [];
    const setWin = !bigWinService.check({ odd, winAmount });

    result.balls.slice(0, 4).forEach((ball, index) => {
      leftCrackStates.push(ball.crackedCount);
      if (ball.odd > 0) {
        leftOdds.push(ball.odd);
        leftBrokenBallsIndexes.push(index);
      }
    });

    result.balls.reverse().slice(0, 4).forEach((ball, index) => {
      rightCrackStates.push(ball.crackedCount);
      if (ball.odd > 0) {
        rightOdds.push(ball.odd);
        rightBrokenBallsIndexes.push(index);
      }
    });
    this.leftAdditionalBalls = additionalInfo.balls ? additionalInfo.balls.slice(0, leftBrokenBallsIndexes.length) : [];
    this.rightAdditionalBalls = additionalInfo.balls ? additionalInfo.balls.slice(additionalInfo.balls.length - rightBrokenBallsIndexes.length) : [];

    this.brokenBallsCount = leftBrokenBallsIndexes.length + rightBrokenBallsIndexes.length;
    return Promise.all([
      this.breakBallsAnimation(
        ballSides.left,
        leftBrokenBallsIndexes,
        this.leftBallAnimation,
        leftOdds,
        leftCrackStates,
        multiplier,
      ),
      this.breakBallsAnimation(
        ballSides.right,
        rightBrokenBallsIndexes,
        this.rightBallAnimation,
        rightOdds,
        rightCrackStates,
        multiplier,
      ),
    ]);
  }

  setBall({ color, scale, onComplete }) {
    this._color = color;
    ACTIVE_ODD.next(this._color);
    this._ballState = bonusSpinService.bonusSpinState ? BONUS_BALL_CRACK.getValue() : BallCrackType.none;
    this.ballContainer.removeChildren();
    this.setPosition(this.ballContainer, this.positions.center);
    this.ball = ballHelper.getBall(this._color, this._ballState);
    this.ball.scale.set(0);
    gsap.to(this.ball, { duration: .05, pixi: { scale }, onComplete });
    this.ballContainer.addChild(this.ball);
    this.container[GameLayers.ball].addChild(this.ballContainer);
  }

  _setBonusBalls(balls, cracksState = []) {
    this.bonusBalls = balls;
    this.bonusLeftBalls = balls.slice(0, 4);
    this.bonusRightBalls = balls.slice(4).reverse();
    const leftCracksState = cracksState.slice(0, 4);
    const rightCracksState = cracksState.slice(4).reverse();

    return Promise.all([
      this._setLeftBalls(this.bonusLeftBalls, 4, leftCracksState),
      this._setRightBalls(this.bonusRightBalls, 4, rightCracksState),
    ]);
  }

  setBonusGameBalls(balls, cracksState = []) {
    Promise.all([
      this._removeLeftBalls(),
      this._removeRightBalls(),
      this._removeBall(),
    ]).then(() => {
      delay(() => {
        this.container[GameLayers.balls].removeChildren();
        this._setBonusBalls(balls, cracksState).then(() => {
          this.setBall(BallType.bonus);
          bonusSpin();
        });
      }, 700);
    });
  }

  removeBonusBalls() {
    return Promise.all([
      this._removeLeftBalls(4),
      this._removeRightBalls(4),
      this._removeBall(),
    ]).then(() => {
      this.container[GameLayers.balls].children = [];
    });
  }

  _removeLeftBalls(step = 0) {
    return new Promise((resolve) => {
      if (!this.leftBalls.length) {
        resolve(true);
      } else {
        this.leftBalls.forEach((ball, index, { length }) => {
          this.leftBallAnimation.moveElementsBack(ball, 0, index + step, .15 * (length - index), -8).then(() => {
            if (index === length - 1) {
              this.leftBalls = [];
              resolve(true);
            }
          });
        });
      }
    });
  }

  _removeRightBalls(step = 0) {
    return new Promise((resolve) => {
      if (!this.rightBalls.length) {
        resolve(true);
      } else {
        this.rightBalls.forEach((ball, index, { length }) => {
          this.rightBallAnimation.moveElementsBack(ball, 0, index + step, .15 * (length - index), 8).then(() => {
            if (index === length - 1) {
              this.rightBalls = [];
              resolve(true);
            }
          });
        });
      }
    });
  }

  _removeBall() {
    return new Promise((resolve) => {
      gsap.to(
        this.ball, {
          duration: .5,
          pixi: { scale: 0 },
          onComplete: () => {
            resolve(true);
          },
        })
      ;
    });
  }

  updateBall() {
    throw new Error(`Implement updateBall method in ${this.constructor.name}`);
  }
}
